import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

/*
 * Affichage d'une question : badge de bloc, intitule, reponses.
 * Deux modes : choix unique (boutons) ou echelle (curseur + continuer).
 */

const ChoiceOptions = ({ current, selectedOption, onChoice }) => (
  <div className="mt-7 grid grid-cols-1 gap-3" role="group" aria-label={current.text}>
    {current.options.map((option) => {
      const selected = selectedOption === option.value;
      return (
        <button
          key={option.value}
          type="button"
          data-testid="diagnostic-answer-option"
          data-value={option.value}
          onClick={() => onChoice(option.value)}
          aria-pressed={selected}
          className={`min-h-[56px] w-full rounded-2xl border px-4 py-3.5 text-left text-sm sm:text-base font-medium transition-colors duration-150 active:scale-[0.99] flex items-center justify-between gap-3 ${
            selected
              ? "border-[#235277] ring-2 ring-[#EAFE63] bg-[#E2F6FF] text-[#0B2B44]"
              : "border-[rgba(35,82,119,0.16)] bg-white text-[#333333] hover:border-[rgba(35,82,119,0.4)] hover:bg-[rgba(226,246,255,0.4)]"
          }`}
        >
          <span>{option.label}</span>
          {selected && <Check className="w-5 h-5 shrink-0 text-[#235277]" aria-hidden="true" />}
        </button>
      );
    })}
  </div>
);

const ScaleInput = ({ current, scaleValue, onScaleChange, onContinue }) => (
  <div className="mt-10">
    <div className="text-center mb-8">
      <span className="font-heading inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#235277] text-[#EAFE63] text-2xl font-bold tabular-nums" data-testid="diagnostic-scale-value">
        {scaleValue}
      </span>
    </div>
    <Slider
      data-testid="diagnostic-scale-slider"
      value={[scaleValue]}
      onValueChange={(values) => onScaleChange(values[0])}
      min={current.min}
      max={current.max}
      step={1}
      aria-label={current.text}
      className="[&_[role=slider]]:w-7 [&_[role=slider]]:h-7 [&_[role=slider]]:border-2 [&_[role=slider]]:border-[#235277] [&_[role=slider]]:bg-white"
    />
    <div className="flex justify-between mt-3 text-xs text-[#9E9E9E]">
      <span>{current.min} : {current.min_label}</span>
      <span>{current.max} : {current.max_label}</span>
    </div>
    <Button
      data-testid="diagnostic-continue-button"
      onClick={onContinue}
      className="mt-10 w-full min-h-[52px] rounded-xl bg-[#235277] hover:bg-[#1E4767] font-heading font-semibold text-base active:scale-[0.98] transition-colors duration-200"
    >
      Continuer
    </Button>
  </div>
);

export const QuestionCard = ({ current, blockLabel, selectedOption, onChoice, scaleValue, onScaleChange, onScaleContinue }) => (
  <>
    <span className="inline-block text-[11px] font-semibold tracking-wide uppercase px-3 py-1 rounded-full bg-[#E2F6FF] text-[#235277] border border-[rgba(35,82,119,0.16)]">
      {blockLabel}
    </span>
    <h1 className="font-heading mt-4 text-xl sm:text-2xl font-semibold leading-snug text-[#060606]" data-testid="diagnostic-question-text">
      {current.text}
    </h1>
    {current.note ? (
      <p className="mt-2 text-xs leading-relaxed text-[#9E9E9E]" data-testid="diagnostic-question-note">
        {current.note}
      </p>
    ) : null}
    {current.type === "choice" ? (
      <ChoiceOptions current={current} selectedOption={selectedOption} onChoice={onChoice} />
    ) : (
      <ScaleInput current={current} scaleValue={scaleValue} onScaleChange={onScaleChange} onContinue={onScaleContinue} />
    )}
  </>
);
